import uuid from 'uuid/v4';
import config from 'config';

import { pick } from 'lodash';
import errorAction from 'shared/utils/errorAction';
import createAsyncAction from 'shared/utils/createAsyncAction';
import readAsBase64 from 'shared/utils/readAsBase64';
import { addToast } from 'modules/messageSystem/actions/addToast';
import { addModal } from 'modules/messageSystem/actions/addModal';
import { GLOBAL_ERRORS_CODE_LIST } from 'modules/session/constants/globalErrorCodes';
import { getFileExtensionFromFileName, getFileExtensionFromFileType } from 'modules/messageSystem/utils/fileUpload';
import * as modalCodes from 'modules/messageSystem/constants/modalCodes';

import createAction from './';
import { showRetryUploadMetaPopup } from './retryUploadConsultationMedia';
import * as services from '../services/uploadConsultationMedia';
import { MODAL_UPLOAD_EXTENSION_FAILED, MODAL_UPLOAD_SIZE_FAILED } from '../../messageSystem/constants/modalCodes';
import mediaStatus from '../constants/mediaStatus';

export const UPLOAD_CONSULTATION_MEDIA = createAsyncAction(createAction('UPLOAD_CONSULTATION_MEDIA'));


const startUpload = (jwt, body) => {
  const headers = {
    'authentication': `Bearer ${ jwt }`,
    'Accept': 'application/json',
  };

  return fetch(config.api_server, {
    method: 'post',
    headers,
    body,
  })
    .then(r => r.json());
};

export const uploadConsultationMedia = (file, base64, comment = '', appointmentId, fileId) => (dispatch) => {
  const jwt = localStorage.getItem('jwt');
  const body = new FormData();
  const timestamp = Date.now();

  const { type, name, size } = pick(file);
  const meta = {
    appointmentId,
    timestamp,
    metaDataId: null,
    id: fileId || uuid(),
    status: mediaStatus.LOADING,
    mimeType: type,
    original: file,
    thumbnail: base64,
    comment,
    name,
    size,
  };

  // check for fileId to prevent duplicates while re-uploading files
  if (!file) {
    return dispatch(showRetryUploadMetaPopup(meta));
  }

  dispatch({
    type: UPLOAD_CONSULTATION_MEDIA.pending,
    meta,
  });

  body.append('query', services.UPLOAD_CONSULTATION_MEDIA(appointmentId, comment.replace(/[\\"]/g, '')));
  body.append('file', file, name);

  return startUpload(jwt, body)
    .then((payload) => {
      if (payload.errors) throw payload.errors[0];

      dispatch({
        type: UPLOAD_CONSULTATION_MEDIA.success,
        payload: {
          ...payload.data.uploadFile,
          id: meta.id,
        },
      });

      return dispatch(addToast('info', 'file-uploaded-successfully-toast', {
        id: 'schedule.patientInfoTab.media.waitingForApproval',
        defaultMessage: '** File uploaded succesfully and now waiting for approval.',
      }));
    })
    .catch((err) => {
      dispatch(errorAction(UPLOAD_CONSULTATION_MEDIA.failure, err, meta));

      if (!GLOBAL_ERRORS_CODE_LIST.includes(err.errorCode)) {
        return dispatch(showRetryUploadMetaPopup(meta));
      }

    });
};

export const selectAndStartUploadMedia = (files, onOk) => (dispatch, getState) => {
  const state = getState();
  const { fileMetaDataConfig } = state.appointmentInfo.patientMedia;
  const { appointmentId } = state.appointmentInfo.currentAppointment;

  const file = files[0];

  if (!fileMetaDataConfig.some(item => item.mimeType === file.type)) {
    const fileType = getFileExtensionFromFileName(file.name) ||
      getFileExtensionFromFileType(file.type) || 'file';

    return dispatch(addModal({
      testId: 'upload-extension-failed-modal',
      typeId: MODAL_UPLOAD_EXTENSION_FAILED,
      preventClosingWhenDismiss: true,
      canAddDuplicate: false,
      isForce: true,
      bodyValues: { fileType },
    }));
  }

  const { maxSizeBytes } = fileMetaDataConfig.find(item => item.mimeType === file.type);
  const maxSizeMB = maxSizeBytes / 1024 / 1024;

  if (file.size > maxSizeBytes) {
    const fileType = file.type.split('/')[1];

    return dispatch(addModal({
      testId: 'upload-size-failed-modal',
      typeId: MODAL_UPLOAD_SIZE_FAILED,
      preventClosingWhenDismiss: true,
      canAddDuplicate: false,
      isForce: true,
      bodyValues: { fileType, maxSizeMB },
    }));
  }

  return readAsBase64(file)
    .then((base64) => dispatch(addModal({
      testId: 'update-file-name-modal',
      typeId: modalCodes.MODAL_UPDATE_UPLOAD_FILE,
      bodyValues: {
        fileName: file.name,
        file,
      },
      onOk: onOk
        ? (fileName) => onOk(base64, fileName, file)
        : (fileName) => dispatch(uploadConsultationMedia(file, base64, fileName, appointmentId)),
      canAddDuplicate: false,
      isForce: true,
    })));
};
